import { domainsWithWhiteText } from "@appcaire/brand";
import { Check } from "lucide-react";
import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../../lib/utils";
import { domainClasses } from "./domains";
import { IconTile } from "./IconTile";
import { roles, type RoleKey } from "./roles";

export interface RoleSwitcherItem {
  role: RoleKey;
  label: ReactNode;
  title: ReactNode;
  text: ReactNode;
  checks: ReactNode[];
}

export interface RoleSwitcherProps {
  items: RoleSwitcherItem[];
  value: RoleKey;
  onChange: (role: RoleKey) => void;
  /** Accessible name of the radio group, e.g. "Jag är…". */
  label: string;
}

/**
 * "Jag är…": chips with radio-group semantics (arrow keys move the
 * selection) and one panel with an XL tile. The selected chip fills with the
 * role's solid. Callers own the state (the website keeps it in #for=<role>).
 */
export function RoleSwitcher({
  items,
  value,
  onChange,
  label,
}: RoleSwitcherProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const index = Math.max(
    0,
    items.findIndex((item) => item.role === value),
  );
  const active = items[index];
  if (!active) return null;
  const { domain, icon } = roles[active.role];
  const d = domainClasses[domain];

  const move = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (!step) return;
    event.preventDefault();
    const next = (index + step + items.length) % items.length;
    onChange(items[next].role);
    refs.current[next]?.focus();
  };

  return (
    <div className="flex flex-col gap-5">
      <div
        role="radiogroup"
        aria-label={label}
        className="flex flex-wrap gap-2"
      >
        {items.map((item, i) => {
          const selected = i === index;
          const itemDomain = domainClasses[roles[item.role].domain];
          return (
            <button
              key={item.role}
              ref={(node) => {
                refs.current[i] = node;
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(item.role)}
              onKeyDown={move}
              className={cn(
                "rounded-full border-[1.5px] px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-caire-ink",
                selected
                  ? // White text only where it passes 4.5:1; human and
                    // attention selections use ink on tint with a solid edge.
                    domainsWithWhiteText.includes(roles[item.role].domain)
                    ? cn(
                        itemDomain.solid,
                        "border-transparent text-caire-on-solid",
                      )
                    : cn(itemDomain.tint, itemDomain.ink, "border-current")
                  : "border-caire-hairline bg-caire-surface text-caire-ink hover:bg-caire-sand",
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      <div
        data-domain={domain}
        className={cn(
          "grid items-center gap-8 rounded-3xl p-8 md:grid-cols-[auto_minmax(0,1fr)]",
          d.tint,
        )}
      >
        <IconTile icon={icon} domain={domain} size="xl" />
        <div className="flex flex-col gap-3">
          <h3
            className={cn(
              "text-[28px] font-extrabold leading-tight tracking-[-0.02em]",
              d.ink,
            )}
          >
            {active.title}
          </h3>
          <p className="max-w-[60ch] text-[17px] leading-normal text-caire-ink/85">
            {active.text}
          </p>
          <ul className="mt-1 flex flex-col gap-2">
            {active.checks.map((check, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[15px]">
                <Check
                  aria-hidden="true"
                  className={cn("mt-0.5 h-[18px] w-[18px] shrink-0", d.ink)}
                  strokeWidth={2.25}
                />
                <span>{check}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
